import React from "react"

import type { SpraypaintQueryOptions } from "../types"
import { useMetersInfinite } from "./use-meters-infinite"

/** Max page size allowed by the API; requests for more return UnsupportedPageSize */
const METERS_PAGE_SIZE = 1000

interface UseAllMetersOptions {
  apiQueryOptions?: SpraypaintQueryOptions
  enabled?: boolean
}

/**
 * Returns every meter matching the query by walking the paged meters endpoint
 * to exhaustion.
 *
 * Use this instead of `useMeters` wherever the result is treated as a complete
 * set -- building a filter's option list, for instance. A single `useMeters`
 * page caps at 1000 meters, so an option backed only by a later meter would be
 * silently missing. `metersIsComplete` stays false until the last page lands,
 * so callers can tell a partial crawl from the whole set.
 *
 * Paging stops on error and the query never throws, so a failed page degrades
 * only the caller rather than tearing down the surrounding error boundary.
 *
 * @example
 * const { metersData, metersIsComplete } = useAllMeters({
 *   apiQueryOptions: { where: { organization_id: organizationId } },
 * })
 */
export const useAllMeters = ({
  apiQueryOptions = {},
  enabled = true,
}: UseAllMetersOptions = {}) => {
  const {
    metersData,
    metersError,
    metersFetchNextPage,
    metersHasNextPage,
    metersIsError,
    metersIsFetching,
    metersIsFetchingNextPage,
  } = useMetersInfinite({
    apiQueryOptions: { ...apiQueryOptions, pageSize: METERS_PAGE_SIZE },
    queryOptions: { enabled, throwOnError: false },
  })

  React.useEffect(() => {
    if (
      !enabled ||
      metersIsError ||
      !metersHasNextPage ||
      metersIsFetchingNextPage
    ) {
      return
    }

    void metersFetchNextPage()
  }, [
    enabled,
    metersFetchNextPage,
    metersHasNextPage,
    metersIsError,
    metersIsFetchingNextPage,
  ])

  return {
    metersData: metersData?.pages.flatMap((page) => page.items),
    metersError,
    // Complete means the last page is in hand, not that nothing is in flight.
    // `isFetching` is true during a background refetch; treating that as
    // incomplete would hide organization resource options and pass an empty
    // meter list even though every page is already cached.
    metersIsComplete:
      enabled && Boolean(metersData) && !metersIsError && !metersHasNextPage,
    metersIsError,
    metersIsFetching,
  }
}
